import { format } from 'date-fns';
import { Car, Lock, ShieldCheck } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import { toast } from 'sonner';
import { useAuth } from '../../../../hooks/AuthenticateContext';
import api from '../../../../services/api';

type VehicleType = 'TRUCK' | 'CARRETA' | 'BITREM';

type Gate = {
  id_operacao_patio_cancela?: number;
  numero_cancela: string | number;
  tipo_cancela?: number;
  iss_id_camera_dianteira?: number;
  [key: string]: any;
};

const CancelaManualSPE: React.FC = () => {
  const { user } = useAuth();
  const [selectedGate, setSelectedGate] = useState<{ gate: Gate; type: 'entry' | 'exit' } | null>(null);
  const [licensePlate, setLicensePlate] = useState('');
  const [vehicleType, setVehicleType] = useState<VehicleType>('TRUCK');
  const [isLoading, setIsLoading] = useState(false);
  const [dbGates, setDbGates] = useState<{ entry: Gate[]; exit: Gate[] }>({ entry: [], exit: [] });

  const isExitGateSelected = selectedGate?.type === 'exit';

  useEffect(() => {
    const fetchGates = async () => {
      try {
        const [entryRes, exitRes] = await Promise.all([
          api.post("/listar/cancelasEntradaSpe", {}).catch(() => ({ data: [] })),
          api.post("/listar/cancelasSaidaSpe", {}).catch(() => ({ data: [] }))
        ]);

        const entryData = entryRes.data?.data || entryRes.data || [];
        const exitData = exitRes.data?.data || exitRes.data || [];
        
        setDbGates({
          entry: Array.isArray(entryData) ? entryData : [],
          exit: Array.isArray(exitData) ? exitData : []
        });
      } catch (error) {
        console.error("Erro ao buscar cancelas SPE:", error);
      }
    };
    fetchGates();
  }, []);

  const getCamIdFromSelectedGate = (selected: { gate: Gate; type: 'entry' | 'exit' }): number | null => {
    const { gate } = selected;
    if (gate.iss_id_camera_dianteira) {
      return Number(gate.iss_id_camera_dianteira);
    }
    if (gate.id_operacao_patio_cancela) {
      return Number(gate.id_operacao_patio_cancela);
    }
    if (gate.numero_cancela) {
      return Number(gate.numero_cancela);
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!selectedGate) {
      toast.warning('Selecione um portão.');
      return;
    }

    if (!licensePlate) {
      toast.warning('Insira a placa do veículo para validação SPE.');
      return;
    }

    const camId = getCamIdFromSelectedGate(selectedGate);
    if (!camId) {
      toast.error('Erro ao identificar a câmera do portão selecionado.');
      return;
    }
    
    const userId = user?.id || 
                   JSON.parse(localStorage.getItem('user') || '{}').id || 
                   1;

    const payload = {
      app: 'SecurOS_SPE',
      spe: true,
      dataHora: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      placa: licensePlate.replace('-', '').toUpperCase(), 
      camId: camId,
      id_usuario_historico: userId
    };

    try {
      setIsLoading(true);
      const response = await api.post('/processaCancelaSpe', payload);
      
      const responseData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

      if (responseData && (responseData.result === true || responseData.success === true)) {
        toast.success('Cancela SPE liberada com sucesso!', {
          duration: 10000,
        });
        setLicensePlate('');
        setSelectedGate(null);
      } else {
        toast.error('Veículo SPE não liberado! Verifique se existe um Agendamento SPE ativo (na entrada) ou se o veículo está no pátio (na saída).', {
          duration: 10000,
        });
      }
    } catch (error: any) {
      console.error('Erro ao abrir cancela SPE:', error);
      toast.error('Erro ao enviar comando de abertura SPE.', {
        duration: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col items-center p-5 overflow-y-auto bg-[#F5F5F5]">
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-lg overflow-hidden flex flex-col flex-1">
        
        {/* Header */}
        <div className="p-8 border-b border-gray-100 flex-shrink-0 bg-blue-50/50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Car className="h-6 w-6 text-[#062D4E]" />
              <h1 className="text-2xl font-bold text-[#062D4E]">Cancela Manual SPE</h1>
            </div>
            <span className="bg-[#062D4E] text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Módulo SPE
            </span>
          </div>
          <p className="text-gray-600 text-sm">
            Selecione o portão e insira a placa para realizar a validação e liberação no fluxo de Operações SPE.
          </p>
        </div>

        <div className="p-8 space-y-8 flex-1 overflow-y-auto">
          
          {/* Gate Selection Section */}
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6 flex items-center gap-2">
              <span className="text-lg">⇄</span> Seleção de Portão SPE
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              
              {/* Entry Gates */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600 font-medium mb-4">
                  <span>→</span> Entrada SPE
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {dbGates.entry.length > 0 ? (
                    dbGates.entry.map((gateItem) => {
                      const isSelected = selectedGate?.type === 'entry' && selectedGate?.gate.id_operacao_patio_cancela === gateItem.id_operacao_patio_cancela;
                      return (
                        <button
                          key={`entry-${gateItem.id_operacao_patio_cancela || gateItem.numero_cancela}`}
                          onClick={() => setSelectedGate({ gate: gateItem, type: 'entry' })}
                          className={`
                            h-16 rounded-lg text-xl font-bold transition-all relative border-2 
                            ${isSelected 
                              ? 'border-[#062D4E] bg-blue-50 text-[#062D4E]' 
                              : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                            }
                          `}
                        >
                          {gateItem.numero_cancela}
                          <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${isSelected ? 'bg-[#062D4E]' : 'bg-gray-300'}`}></div>
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-xs text-gray-400 col-span-3 py-2">Nenhuma cancela de entrada SPE cadastrada</p>
                  )}
                </div>
              </div>

              {/* Exit Gates */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-red-500 font-medium mb-4">
                  <span>←</span> Saída SPE
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {dbGates.exit.length > 0 ? (
                    dbGates.exit.map((gateItem) => {
                      const isSelected = selectedGate?.type === 'exit' && selectedGate?.gate.id_operacao_patio_cancela === gateItem.id_operacao_patio_cancela;
                      return (
                        <button
                          key={`exit-${gateItem.id_operacao_patio_cancela || gateItem.numero_cancela}`}
                          onClick={() => setSelectedGate({ gate: gateItem, type: 'exit' })}
                          className={`
                            h-16 rounded-lg text-xl font-bold transition-all relative border-2 
                            ${isSelected 
                              ? 'border-[#062D4E] bg-blue-50 text-[#062D4E]' 
                              : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                            }
                          `}
                        >
                          {gateItem.numero_cancela}
                          <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${isSelected ? 'bg-[#062D4E]' : 'bg-gray-300'}`}></div>
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-xs text-gray-400 col-span-3 py-2">Nenhuma cancela de saída SPE cadastrada</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Separation Line */}
          <hr className="border-gray-100" />

          {/* Vehicle Data Section */}
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6 flex items-center gap-2">
              <Car className="h-4 w-4" /> Dados do Veículo SPE
            </h3>
            
            <div className={`grid grid-cols-1 gap-8 items-start ${isExitGateSelected ? '' : 'lg:grid-cols-2'}`}>
              {/* Vehicle Type Selection */}
              {!isExitGateSelected && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    Tipo de Veículo
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: 'TRUCK' as VehicleType, label: 'Truck', icon: (active: boolean) => (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 4H15V15H1V4Z" fill={active ? "white" : "#6B7280"} />
                          <path d="M15 8H19L22 11V15H15V8Z" fill={active ? "white" : "#6B7280"} />
                          <circle cx="5" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                          <circle cx="18" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                        </svg>
                      )},
                      { id: 'CARRETA' as VehicleType, label: 'Carreta', icon: (active: boolean) => (
                        <svg width="28" height="24" viewBox="0 0 28 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                           <path d="M2 4H16V15H2V4Z" fill={active ? "white" : "#6B7280"} />
                           <path d="M18 8H21L24 11V15H18V8Z" fill={active ? "white" : "#6B7280"} />
                           <circle cx="5" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                           <circle cx="12" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                           <circle cx="21" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                        </svg>
                      )},
                      { id: 'BITREM' as VehicleType, label: 'Bitrem', icon: (active: boolean) => (
                         <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="0" y="4" width="8" height="11" fill={active ? "white" : "#6B7280"} />
                          <rect x="10" y="4" width="8" height="11" fill={active ? "white" : "#6B7280"} />
                          <path d="M20 8H24L27 11V15H20V8Z" fill={active ? "white" : "#6B7280"} />
                          <circle cx="4" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                          <circle cx="14" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                          <circle cx="23" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                        </svg>
                      )}
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setVehicleType(type.id)}
                        className={`
                          flex flex-col items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all h-28
                          ${vehicleType === type.id
                            ? 'bg-[#005694] border-[#005694] text-white shadow-md'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="transform scale-110">
                          {type.icon(vehicleType === type.id)}
                        </div>
                        <span className="text-sm font-bold">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* License Plate Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 block">
                  Placa do Veículo (Validação SPE)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="bg-gray-400 text-white text-[10px] px-1 rounded font-bold">123</span>
                  </div>
                  <InputMask
                    mask="aaa-9*99"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#062D4E] transition-colors placeholder-gray-300 uppercase h-[52px]"
                    placeholder="AAA-0000"
                    maskChar={null} 
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Entrada: exige Agendamento SPE ativo. Saída: exige entrada e agendamento associados no pátio.
                </p>
              </div>
            </div>
          </section>

          {/* Action Button */}
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className={`w-full bg-[#005694] hover:bg-[#004475] text-white font-bold h-14 rounded-lg flex items-center justify-center gap-3 transition-colors shadow-sm active:scale-[0.99] ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Lock className="h-5 w-5" />
            )}
            {isLoading ? 'ENVIANDO...' : 'ABRIR CANCELA SPE'}
          </button>
          
          <div className="w-full text-center">
            <p className="text-xs text-gray-400">
              Movimentação SPE validada e registrada automaticamente.
            </p>
          </div>

        </div>
        
        {/* Footer Version */}
        <div className="bg-gray-50 px-8 py-3 text-right flex-shrink-0">
          <span className="text-xs text-gray-400">v2.4.1 SPE</span>
        </div>

      </div>
    </div>
  );
};

export default CancelaManualSPE;
